// Clerk frontend config (Identity/Billing roadmap, Phase 1 step 5). DORMANT: CLERK_ENABLED=false ships
// a no-op landing.js, so the marketing page + "Sign in"/"Start free" links behave exactly as today
// (straight to /app behind Cloudflare Access). Flip to true only after the deployed CSP is opened to
// the Clerk domain (see _headers) and the Worker's CLERK_ENABLED + CLERK_SECRET_KEY are in place.
//
// The PUBLISHABLE key is public by design (it ships in the browser). Two keys, chosen at build time:
// pk_test = the dev instance (staging), pk_live = the production instance (prod cutover build).

// Build-time flag: esbuild replaces __CLERK_BUILD__ (false unless the build sets CLERK_BUILD=1). So a
// normal build (tests, prod deploy) ships Clerk OFF; only the staging deploy (CLERK_BUILD=1) turns it
// on. Keeps prod strict without an edit-revert dance.
declare const __CLERK_BUILD__: boolean;
export const CLERK_ENABLED = __CLERK_BUILD__;

// Which Clerk INSTANCE: esbuild replaces __CLERK_LIVE__ (false unless the build sets CLERK_LIVE=1). The
// prod-cutover build sets it, so production talks to the pk_live instance (clerk.studio-moneyball.io),
// while every other build — staging, normal, tests — uses the pk_test dev instance. build.mjs derives
// the CSP Clerk host from whichever key is active (clerkHostFromConfig), so the two never disagree.
declare const __CLERK_LIVE__: boolean;
const PK_TEST = 'pk_test_dGhvcm91Z2gtdGlnZXItMjguY2xlcmsuYWNjb3VudHMuZGV2JA';
const PK_LIVE = 'pk_live_Y2xlcmsuc3R1ZGlvLW1vbmV5YmFsbC5pbyQ';
export const CLERK_PUBLISHABLE_KEY = __CLERK_LIVE__ ? PK_LIVE : PK_TEST;

/** Clerk's Frontend API host, decoded from the publishable key (…_<base64(host$)>). Used to load the
 *  hosted clerk.browser.js and (later) to allow that host in the CSP. */
export function clerkFrontendApi(publishableKey: string): string {
  const b64 = publishableKey.split('_')[2] ?? '';
  try {
    return atob(b64).replace(/\$+$/, '');
  } catch {
    return '';
  }
}

/** Minimal shape of the global Clerk object we use — the landing sign-in modal AND the app shell's
 *  session/token maintenance. Shared so both entries load Clerk exactly the same way. */
export interface ClerkSession {
  id?: string;
  getToken(options?: Record<string, unknown>): Promise<string | null>;
}
/** One active device/session from user.getSessions() (native "Active devices" list). */
export interface ClerkSessionActivity {
  id: string;
  latestActivity?: {
    deviceType?: string;
    browserName?: string;
    city?: string;
    country?: string;
    isMobile?: boolean;
  };
  revoke?: () => Promise<unknown>;
}
/** The subset of the Clerk user we read for the account chip + Account profile card. Clerk serves
 *  `imageUrl` from https://img.clerk.com (already allowed in the app CSP), covering both the Google
 *  avatar and any photo the user uploads via Clerk's own profile UI. */
export interface ClerkUser {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  imageUrl?: string;
  hasImage?: boolean;
  primaryEmailAddress?: { emailAddress?: string } | null;
  /** Update profile fields (we use firstName/lastName for the native name edit). */
  update?: (params: { firstName?: string; lastName?: string }) => Promise<unknown>;
  /** Upload a new avatar (file) or remove it (file: null). Clerk stores + serves it from img.clerk.com. */
  setProfileImage?: (params: { file: File | Blob | null }) => Promise<unknown>;
  /** OAuth connections (Google etc.) — we display "Signed in with Google · <email>" read-only. */
  externalAccounts?: Array<{ provider?: string; emailAddress?: string }>;
  /** Active devices/sessions for the native "Active devices" list. */
  getSessions?: () => Promise<ClerkSessionActivity[]>;
  /** Permanently delete the account (the destructive action, behind a typed confirm in the UI). */
  delete?: () => Promise<unknown>;
}
export interface ClerkGlobal {
  loaded: boolean;
  user?: ClerkUser | null;
  session?: ClerkSession | null;
  load: (opts?: Record<string, unknown>) => Promise<void>;
  openSignIn: (opts?: Record<string, unknown>) => void;
  openSignUp: (opts?: Record<string, unknown>) => void;
  openUserProfile: (opts?: Record<string, unknown>) => void;
  /** Render the full UserProfile (name, photo, email, connected accounts, security, delete) INLINE
   *  into a node we own, instead of a modal — used to embed those fields in our Account section. */
  mountUserProfile: (node: HTMLElement, props?: Record<string, unknown>) => void;
  unmountUserProfile: (node: HTMLElement) => void;
  redirectToSignIn: (opts?: Record<string, unknown>) => void;
  signOut: (opts?: Record<string, unknown>) => Promise<void>;
  /** Fires on user/session changes (e.g. after a photo upload) so the chip can re-render. Optional
   *  because a failed/partial clerk-js load may not expose it. Returns an unsubscribe fn. */
  addListener?: (cb: (payload: unknown) => void) => () => void;
}

/** Brand skin for every Clerk component (the landing sign-in/up modal AND the in-app session UI).
 *  Applied globally via clerk.load() so both match the site: the gold primary button with ink text,
 *  self-hosted Open Sans, and the horizontal logo. Deliberately minimal — a fuller theme lands with the
 *  redesign. The logo path is same-origin ('self'), so it passes the Clerk-build CSP's img-src. */
export const CLERK_APPEARANCE = {
  variables: {
    colorPrimary: '#ffbe17',
    colorTextOnPrimaryBackground: '#1e1e1e',
    colorText: '#1e1e1e',
    borderRadius: '10px',
    fontFamily: '"Open Sans", system-ui, sans-serif',
  },
  layout: {
    logoImageUrl: '/assets/logos/studio-moneyball-light.svg',
    socialButtonsVariant: 'iconButton',
    showOptionalFields: false,
  },
  // Enlarge the logo at the top of the modal (Clerk's default renders it quite small).
  elements: {
    logoBox: { height: '52px' },
    logoImage: { height: '52px' },
  },
};

/** Appearance for the INLINE-mounted UserProfile inside our Account section: strip Clerk's own card
 *  chrome so it blends into the page instead of looking like an imported widget — no drop shadow, no
 *  border/radius, transparent background, and hide its redundant navbar header + the "Secured by
 *  Clerk" footer (our section already has its own heading). Kept separate from CLERK_APPEARANCE so the
 *  sign-in modal is untouched. Some keys are belt-and-suspenders with the .cl-* CSS in app.css. */
export const CLERK_PROFILE_APPEARANCE = {
  variables: CLERK_APPEARANCE.variables,
  elements: {
    rootBox: { width: '100%' },
    cardBox: { width: '100%', maxWidth: 'none', boxShadow: 'none', border: 'none', borderRadius: '0' },
    card: { boxShadow: 'none', border: 'none', borderRadius: '0', background: 'transparent', padding: '0' },
    scrollBox: { boxShadow: 'none', border: 'none', borderRadius: '0', background: 'transparent' },
    navbar: { background: 'transparent', boxShadow: 'none', borderRight: 'none', padding: '0' },
    header: { display: 'none' },
    pageScrollBox: { paddingTop: '0' },
    footer: { display: 'none' },
  },
};

/** Inject Clerk's hosted browser SDK for this publishable key and return the ready Clerk instance.
 *  Bounded by `timeoutMs`: rejects if the key is malformed, the script fails, OR the load stalls past
 *  the timeout — the last case matters because some blockers drop the request silently (no error
 *  event), which would otherwise hang forever. Callers fail open (Access stays / button falls back). */
export async function loadClerk(publishableKey: string, timeoutMs = 5000): Promise<ClerkGlobal> {
  const host = clerkFrontendApi(publishableKey);
  if (!host) throw new Error('bad Clerk publishable key');
  const inner = (async (): Promise<ClerkGlobal> => {
    const win = window as unknown as { Clerk?: ClerkGlobal };
    // Idempotent: if both the landing and the app tried to load it, reuse the one instance.
    if (!win.Clerk) {
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = `https://${host}/npm/@clerk/clerk-js@5/dist/clerk.browser.js`;
        s.async = true;
        s.crossOrigin = 'anonymous';
        s.setAttribute('data-clerk-publishable-key', publishableKey);
        s.addEventListener('load', () => resolve(), { once: true });
        s.addEventListener('error', () => reject(new Error('clerk-js failed to load')), { once: true });
        document.head.appendChild(s);
      });
    }
    const clerk = win.Clerk;
    if (!clerk) throw new Error('window.Clerk missing after load');
    if (!clerk.loaded) await clerk.load({ appearance: CLERK_APPEARANCE });
    return clerk;
  })();
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('clerk-js load timed out')), timeoutMs),
  );
  return Promise.race([inner, timeout]);
}
